<script lang="ts">
import '@/types/tower/v2/CustomFabricClasses';
import { TowerInfo } from './AccountTowerVisualizer';
import { AppState, Commit } from '@/store';
import { StoreMutationType } from '@/types/StoreMutationType';
import { nextTick } from 'vue';

export default {
  name: 'TowerCanvas',
};
</script>

<script setup lang="ts">
import { fabric } from 'fabric';
import { ProgramTower } from '@/types/tower/v2/Tower';
import { markRaw, watch, inject, ref, onMounted } from 'vue';
import { useStore } from 'vuex';
import CanvasUtils from '@/utilities/tower/v2/Canvas';

const props = defineProps<{
  width: number;
  height: number;
  tower: ProgramTower;
}>();

let canvas = markRaw({} as fabric.Canvas);

// the current serialized canvas state
const currentCanvas = ref<string>('');

const towerInfo = inject<TowerInfo>('towerInfo');
const store = useStore<AppState>();

// ----------------
// lifecycle hooks
// ----------------
onMounted(() => {
  canvas = new fabric.TowerCanvas('c', {
    enableRetinaScaling: false,
    stopContextMenu: true,
    fireRightClick: true,
  });

  if (towerInfo?.canvas) {
    towerInfo.canvas.value = canvas;
  }

  canvas.on('selection:created', (e: fabric.IEvent) => {
    if (e.selected?.length === 1 && e.selected[0] instanceof fabric.GroupBase && towerInfo) {
      towerInfo.selectedTower.value = e.selected[0].towerCard;
    }
  });
  canvas.on('selection:updated', (e: fabric.IEvent) => {
    if (e.selected?.length === 1 && e.selected[0] instanceof fabric.GroupBase && towerInfo) {
      towerInfo.selectedTower.value = e.selected[0].towerCard;
    }
  });
  canvas.on('selection:cleared', () => {
    if (towerInfo) {
      towerInfo.selectedTower.value = undefined;
    }
  });
});

// ----------
// watchers
// ----------
// ensure width and height have been set correctly before loading tower
// this allows loadTower to center the tower on the page and scale height properly
watch(
  [() => props.width, () => props.height], 
  ([width, height]) => {
    if (width > 0 && height > 0) {
      nextTick(() => {
        canvas.setWidth(width);
        canvas.setHeight(height);
        if (props.tower && canvas.isEmpty()) {
          loadTower(props.tower);
        }
      });
    }
  }, 
  { immediate: true }  // This ensures the watcher is triggered on initial render as well
);

watch(
  () => store.state.towerCanvasState.zoomLevel,
  (zoomLevel) => {
    handleZoom(zoomLevel);
  },
);

// ----------
// functions
// ----------
function loadTower(tower: ProgramTower): void {
  try {
    // set up resize bounds for scaling logic below
    const resizeWidth = canvas.getWidth() * 0.9;
    const resizeHeight = canvas.getHeight() * 0.9;

    canvas.loadFromJSON(tower.towerObject, () => {
      canvas.getObjects().forEach((obj) => {
        obj.set({
          includeDefaultValues: false,
        });

        const objWidth = obj.width ?? 1;
        const objHeight = obj.height ?? 1;

        // scale object so width and height properly fit on screen
        if (objWidth > resizeWidth || objHeight > resizeHeight) {
          const scaleWidth = resizeWidth / objWidth;
          const scaleHeight = resizeHeight / objHeight;
          const scale = Math.min(scaleWidth, scaleHeight);

          obj.scale(scale);
        }
    
        canvas.centerObject(obj); // make sure objects are centered after scaling
      });
      canvas.renderAll();
      
      currentCanvas.value = JSON.stringify(canvas.toJSON());
      Commit(StoreMutationType.SET_TOWER_CANVAS_ZOOM_LEVEL, canvas.getZoom() ?? CanvasUtils.DEFAULT_ZOOM);
    });
  } catch (e) {
    console.error(e);
  }
}

function handleZoom(zoomLevel?: number): void {
  if (!canvas || !zoomLevel) return;

  const center = canvas.getCenter() ?? { left: 0, top: 0 };
  const centerPoint = new fabric.Point(center.left, center.top);

  canvas.zoomToPoint(centerPoint, zoomLevel);
  canvas.getObjects().forEach((obj) => obj.setCoords());
  canvas.requestRenderAll();
}
</script>

<template>
  <canvas
    id="c"
    class="absolute z-10"
    :style="{ width: `${width}px`, height: `${height}px` }"
    :width="width"
    :height="height"
  >
  </canvas>
</template>
